/**
 * @fileoverview Zod schema for Plan entities (Supabase rows/DTOs).
 * Updated to match new versioned plans schema with risk management and plan_actions.
 */

import { z } from 'zod';

// Risk schema for JSONB column
const RiskZ = z
  .object({
    desc: z.string().min(1).optional(),
    prob: z.number().min(0).max(1).optional(),
    impact: z.number().min(0).max(1).optional(),
    mitigation: z.string().optional(),
  })
  .passthrough();

// Milestone schema for JSONB column
const MilestoneZ = z
  .object({
    name: z.string().min(1).optional(),
    due: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
    exit: z.string().optional(),
  })
  .passthrough();

// Dependency schema for JSONB column
const DependencyZ = z
  .object({
    ref: z.string().optional(),
    note: z.string().optional(),
  })
  .passthrough();

// Assumption schema (array of strings in JSONB)
const AssumptionZ = z.string();

// Plan action schema for JSONB column
// Made lenient for backwards compatibility with old data
const PlanActionZ = z.discriminatedUnion('type', [
  z
    .object({
      type: z.literal('action'),
      label: z.string().min(1).optional(),
      target: z
        .object({
          unit: z.enum(['per_day', 'per_week', 'per_month']).optional(),
          exact: z.number().optional(),
          min: z.number().optional(),
          max: z.number().optional(),
        })
        .optional(),
      schedule: z
        .object({
          period: z.enum(['day', 'week', 'month']).optional(),
          days: z.array(z.string()).optional(), // ["Mon", "Thu"] etc.
        })
        .optional(),
      autogenerate: z.boolean().optional(),
      task_payload: z
        .object({
          tags: z.array(z.string()).optional(),
          duration_min: z.number().optional(),
          notes: z.string().optional(),
        })
        .optional(),
    })
    .passthrough(),
  z
    .object({
      type: z.literal('step'),
      label: z.string().min(1).optional(),
      due: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
      autogenerate: z.boolean().optional(),
      task_payload: z
        .object({
          tags: z.array(z.string()).optional(),
          duration_min: z.number().optional(),
          notes: z.string().optional(),
        })
        .optional(),
    })
    .passthrough(),
]);

export const PlanSchema = z
  .object({
    // Core identity
    id: z.string().uuid(),
    user_id: z.string().uuid(),

    // Versioning fields
    plan_lineage: z.string().uuid(),
    plan_version: z.number().int().nonnegative(),
    previous_plan_version: z.number().int().nonnegative().nullable().optional(),
    is_current: z.boolean(),
    revision: z.number().int().nonnegative(),

    // Goal linkage
    linked_goal_id: z.string().uuid(),
    linked_goal_title: z.string().nullable().optional(),
    origin_goal_id: z.string().uuid().nullable().optional(),
    origin_goal_lineage: z.string().uuid().nullable().optional(),
    origin_goal_version: z.number().int().nullable().optional(),

    // Timeline
    start_date: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .nullable()
      .optional(),
    end_date: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .nullable()
      .optional(),
    duration_weeks: z.number().int().nullable().optional(),

    // Risk management (optional for backwards compatibility)
    risks: z.array(RiskZ).optional(),
    risk_score: z.number().optional(),

    milestones: z.array(MilestoneZ).optional(),
    dependencies: z.array(DependencyZ).optional(),
    assumptions: z.array(AssumptionZ).optional(),
    plan_actions: z.array(PlanActionZ).optional(),
    tasks_total: z.number().int().nonnegative().optional(),
    tasks_done: z.number().int().nonnegative().optional(),
    completion_rate: z.number().nullable().optional(),

    // Metadata
    plan_change_notes: z.string().nullable().optional(),
    last_idempotency_key: z.string().nullable().optional(),
    created_at: z.string().nullable().optional(),
    updated_at: z.string().nullable().optional(),
    deleted_at: z.string().nullable().optional(),
  })
  .passthrough();

export type Plan = z.infer<typeof PlanSchema>;
export function parsePlan(input: unknown): Plan {
  return PlanSchema.parse(input);
}

export const PlanArraySchema = z.array(PlanSchema);
export type PlanArray = z.infer<typeof PlanArraySchema>;
